import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { GatewayNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/outputs/gateway-network.model';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';

interface NetworkBillingDetailsDetailsViewProps {
  billingDetails: GatewayNetworkModel['billingDetails'];
}

export const NetworkBillingDetailsDetailsView = ({
  billingDetails,
}: NetworkBillingDetailsDetailsViewProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <Card flex={1} gap={GapEnum.L}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="bank"
        variant={AccentVariantEnum.Accent2}
        text={t('delmonicos-shared-sdk.outputs-network.billing-details')}
      />
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-billing-details.first-name')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>{billingDetails.firstName}</span>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-billing-details.last-name')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>{billingDetails.lastName}</span>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-billing-details.address')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>
            {billingDetails.address.address}, {billingDetails.address.zipCode}{' '}
            {billingDetails.address.city} - {billingDetails.address.countryCode}
          </span>
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-billing-details.iban')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>{billingDetails.iban}</span>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-billing-details.bic')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>{billingDetails.bic}</span>
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={GapEnum.S} alignItems="center">
        <Text bold>
          {t('delmonicos-shared-sdk.outputs-billing-details.mandate')}
        </Text>
      </Stack>
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>
            {billingDetails.mandate?.dateOfSignature
              ? formatDate(billingDetails.mandate.dateOfSignature, i18n)
              : '-'}
          </span>
        </Stack>
      </Stack>
      <Stack gap={GapEnum.S}>
        <Stack flex={1}>
          <Text fontSize={TextFontSize.Small} uppercase>
            {t('delmonicos-shared-sdk.outputs-sepa-mandate.document-number')}
          </Text>
        </Stack>
        <Stack flex={1}>
          <span>{billingDetails.mandate?.documentNumber ?? '-'}</span>
        </Stack>
      </Stack>
    </Card>
  );
};
